import { decryptData, encryptData } from "@/features/cryptography";
import type { EncryptedSeveritiesAsset } from "@/store/services/supabase";
import type { SecretSalt } from "@/types/cryptography";
import { returnPeriods } from "@/types/returnPeriod";
import { hexToArrayBuffer } from "@/utils/crypto";
import type { AnomalyManagamentSupportedHazards } from "@/utils/hazards";
import { v4 } from "uuid";
import { SEVERITIES_YEAR_SELECTION } from "../constants";
import type { SeveritiesRunHazardOptions } from "../types";
import type { SeveritiesRow } from "./validation";

/** A decrypted `severities_assets` row, as `encryptSeveritiesAsset` stored it. */
export interface DecryptedSeveritiesAsset {
  id: string;
  itemId: string;
  coordinates: { latitude: number; longitude: number; elevation?: number };
  data: SeveritiesRunHazardOptions & {
    userSuppliedId?: string;
    latitude: number;
    longitude: number;
    elevation?: number;
  };
}

/** An uploaded row as a `severities_assets` insert, with the run's settings in `data`. */
export const encryptSeveritiesAsset = async (
  secret: SecretSalt,
  severityId: string,
  { user_supplied_id, latitude, longitude, elevation }: SeveritiesRow,
  settings: SeveritiesRunHazardOptions
) => ({
  severity_id: severityId,
  encrypted_id: await encryptData(secret, v4()),
  coordinates: await encryptData(secret, JSON.stringify({ latitude, longitude, elevation })),
  data: await encryptData(
    secret,
    JSON.stringify({ userSuppliedId: user_supplied_id, latitude, longitude, elevation, ...settings })
  ),
});

export const decryptSeveritiesAsset = async (
  secret: SecretSalt,
  asset: EncryptedSeveritiesAsset
): Promise<DecryptedSeveritiesAsset> => {
  if (!asset.coordinates || !asset.data) throw new Error(`Severities asset ${asset.id} is missing its data`);

  const decrypt = (hex: string) => decryptData(secret, hexToArrayBuffer(hex));

  return {
    id: asset.id,
    itemId: await decrypt(asset.encrypted_id),
    coordinates: JSON.parse(await decrypt(asset.coordinates)),
    data: JSON.parse(await decrypt(asset.data)),
  };
};

interface SeveritiesBatchItemInput {
  itemId: string;
  coordinates: { latitude: number; longitude: number; elevation?: number };
  settings: SeveritiesRunHazardOptions;
}

/** One line of a `severities/batch` request: every hazard and return period, with the run's settings. */
export const toSeveritiesBatchItem = ({ itemId, coordinates, settings }: SeveritiesBatchItemInput) => {
  const returnPeriodsOptions = { return_periods: returnPeriods };
  const withOptions = (hazard: keyof AnomalyManagamentSupportedHazards) => ({
    ...returnPeriodsOptions,
    options: {
      use_defended_areas: settings.defences[hazard],
      asset_lifting_rp: settings.anomaly_management_rp[hazard],
    },
  });

  return {
    item_id: itemId,
    coordinates,
    hazards: {
      coastal_inundation: withOptions("coastal_inundation"),
      cyclone_storm_surge: withOptions("cyclone_storm_surge"),
      cyclone_wind: returnPeriodsOptions,
      flood_riverine: withOptions("flood_riverine"),
      flood_surfacewater: withOptions("flood_surfacewater"),
      heat: returnPeriodsOptions,
      wind: returnPeriodsOptions,
    },
    years: SEVERITIES_YEAR_SELECTION,
    scenarios: ["rcp85"],
  };
};
